import React, { useState, useEffect } from 'react';
import { ChevronRight, Save, Send, Trash2 } from 'lucide-react';
import axios from 'axios';
import config from '../config/config';

export default function SponsorshipSurvey() {
  const [formData, setFormData] = useState({
    projectName: '',
    phoneNo: '',
    name: '',
    dob: '',
    class: '',
    healthStatus: '',
    hobbiesAndLikes: '',
    areaOfResidence: '',
    motherName: '',
    motherAge: '',
    motherOccupation: '',
    motherHealthStatus: '',
    fatherName: '',
    fatherAge: '',
    fatherOccupation: '',
    fatherHealthStatus: '',
    guardianName: '',
    guardianAge: '',
    guardianOccupation: '',
    siblings: [{ name: '', age: '', class: '' }],
    otherFamilyMembers: ['', '', ''],
    newFamiliesInfo: '',
    sourceOfIncome: '',
    houseCondition: '',
    mainReasons: '',
  });

  const [currentSection, setCurrentSection] = useState(0);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'projectName':
      case 'name':
      case 'class':
      case 'healthStatus':
      case 'hobbiesAndLikes':
      case 'areaOfResidence':
      case 'sourceOfIncome':
      case 'houseCondition':
      case 'mainReasons':
        return value.trim() ? '' : 'This field is required';
      case 'phoneNo':
        return /^[0-9+\-() ]{7,20}$/.test(value) ? '' : 'Invalid phone number';
      case 'dob':
        return value && new Date(value) <= new Date() ? '' : 'Invalid or future date';
      case 'motherAge':
      case 'fatherAge':
      case 'guardianAge':
        return !value || (Number(value) > 0 && Number(value) <= 120) ? '' : 'Age must be between 1 and 120';
      case 'siblings':
        return value.every(sibling => 
          sibling.name.trim() && 
          Number(sibling.age) > 0 && Number(sibling.age) <= 120 && 
          sibling.class.trim()
        ) ? '' : 'All sibling fields are required and age must be between 1 and 120';
      default:
        return '';
    }
  };

  // Real-time validation
  useEffect(() => {
    const newErrors = {};
    Object.keys(formData).forEach(field => {
      if (field === 'siblings') {
        newErrors[field] = validateField(field, formData.siblings);
      } else if (!['motherName', 'motherOccupation', 'motherHealthStatus', 'fatherName', 'fatherOccupation', 'fatherHealthStatus', 'guardianName', 'guardianOccupation', 'newFamiliesInfo', 'otherFamilyMembers'].includes(field)) {
        newErrors[field] = validateField(field, formData[field]);
      }
    });
    setErrors(newErrors);
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSiblingChange = (index, field, value) => {
    const newSiblings = [...formData.siblings];
    newSiblings[index] = { ...newSiblings[index], [field]: value };
    setFormData(prev => ({ ...prev, siblings: newSiblings }));
  };

  const addSibling = () => {
    setFormData(prev => ({
      ...prev,
      siblings: [...prev.siblings, { name: '', age: '', class: '' }],
    }));
  };

  const removeSibling = (index) => {
    if (formData.siblings.length > 1) {
      setFormData(prev => ({
        ...prev,
        siblings: prev.siblings.filter((_, i) => i !== index),
      }));
    } else {
      setErrors(prev => ({ ...prev, siblings: 'At least one sibling entry is required' }));
    }
  };

  const handleOtherFamilyChange = (index, value) => {
    const newMembers = [...formData.otherFamilyMembers];
    newMembers[index] = value;
    setFormData(prev => ({ ...prev, otherFamilyMembers: newMembers }));
  };

  const sections = [
    { title: 'Basic Information', fields: ['projectName', 'phoneNo', 'name', 'dob', 'class', 'healthStatus', 'hobbiesAndLikes', 'areaOfResidence'] },
    { title: 'Family Information', fields: ['mother', 'father', 'guardian'] },
    { title: 'Siblings & Other Members', fields: ['siblings', 'other'] },
    { title: 'Economic & Housing', fields: ['economic'] },
  ];

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    if (match) return decodeURIComponent(match[2]);
    return null;
  };

  const handleSubmit = async (status) => {
    // Check for validation errors before submitting
    const hasErrors = Object.values(errors).some(error => error);
    if (hasErrors) {
      setErrors(prev => ({ ...prev, form: 'Please fix all validation errors before submitting' }));
      alert('Please fix all validation errors before submitting');
      return;
    }

    try {
      const payload = { ...formData, status };
      const token = localStorage.getItem('token');
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (!xsrfToken) throw new Error('CSRF token not found');

      const response = await axios.post(`${config.API_URL}/api/sponsorship-applications`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-XSRF-TOKEN': xsrfToken,
        },
        withCredentials: true,
      });

      setSuccess(response.data.message);
      setErrors({});
      alert(`Sponsorship report ${status === 'draft' ? 'saved as draft' : 'submitted'} successfully!`);
      console.log('Form submitted:', formData);
    } catch (err) {
      setErrors({ form: 'Failed to save application. Please try again.' });
      setSuccess(null);
      console.error('Submission error:', err);
      alert('Failed to save application. Please try again.');
    }
  };

  const renderSection = () => {
    switch (currentSection) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                <input
                  type="text"
                  value={formData.projectName}
                  onChange={(e) => handleInputChange('projectName', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.projectName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Enter project name"
                />
                {errors.projectName && <p className="text-red-500 text-sm mt-1">{errors.projectName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone No</label>
                <input
                  type="tel"
                  value={formData.phoneNo}
                  onChange={(e) => handleInputChange('phoneNo', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.phoneNo ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Enter phone number"
                />
                {errors.phoneNo && <p className="text-red-500 text-sm mt-1">{errors.phoneNo}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter full name"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleInputChange('dob', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.dob ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.dob && <p className="text-red-500 text-sm mt-1">{errors.dob}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
                <input
                  type="text"
                  value={formData.class}
                  onChange={(e) => handleInputChange('class', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.class ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Enter class"
                />
                {errors.class && <p className="text-red-500 text-sm mt-1">{errors.class}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Health Status</label>
              <input
                type="text"
                value={formData.healthStatus}
                onChange={(e) => handleInputChange('healthStatus', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.healthStatus ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter health status"
              />
              {errors.healthStatus && <p className="text-red-500 text-sm mt-1">{errors.healthStatus}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hobbies and Likes</label>
              <textarea
                value={formData.hobbiesAndLikes}
                onChange={(e) => handleInputChange('hobbiesAndLikes', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.hobbiesAndLikes ? 'border-red-500' : 'border-gray-300'}`}
                rows="3"
                placeholder="Enter hobbies and interests"
              />
              {errors.hobbiesAndLikes && <p className="text-red-500 text-sm mt-1">{errors.hobbiesAndLikes}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Area of Residence</label>
              <input
                type="text"
                value={formData.areaOfResidence}
                onChange={(e) => handleInputChange('areaOfResidence', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.areaOfResidence ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter area of residence"
              />
              {errors.areaOfResidence && <p className="text-red-500 text-sm mt-1">{errors.areaOfResidence}</p>}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-8">
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Mother's Information</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={formData.motherName}
                  onChange={(e) => handleInputChange('motherName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                  placeholder="Mother's name"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    value={formData.motherAge}
                    onChange={(e) => handleInputChange('motherAge', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${errors.motherAge ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Age"
                  />
                  {errors.motherAge && <p className="text-red-500 text-sm mt-1">{errors.motherAge}</p>}
                  <input
                    type="text"
                    value={formData.motherOccupation}
                    onChange={(e) => handleInputChange('motherOccupation', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                    placeholder="Occupation"
                  />
                </div>
                <input
                  type="text"
                  value={formData.motherHealthStatus}
                  onChange={(e) => handleInputChange('motherHealthStatus', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                  placeholder="Health status"
                />
              </div>
            </div>

            <div className="bg-green-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Father's Information</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => handleInputChange('fatherName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                  placeholder="Father's name"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    value={formData.fatherAge}
                    onChange={(e) => handleInputChange('fatherAge', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${errors.fatherAge ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Age"
                  />
                  {errors.fatherAge && <p className="text-red-500 text-sm mt-1">{errors.fatherAge}</p>}
                  <input
                    type="text"
                    value={formData.fatherOccupation}
                    onChange={(e) => handleInputChange('fatherOccupation', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                    placeholder="Occupation"
                  />
                </div>
                <input
                  type="text"
                  value={formData.fatherHealthStatus}
                  onChange={(e) => handleInputChange('fatherHealthStatus', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                  placeholder="Health status"
                />
              </div>
            </div>

            <div className="bg-purple-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Guardian's Information</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={formData.guardianName}
                  onChange={(e) => handleInputChange('guardianName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                  placeholder="Guardian's name"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    value={formData.guardianAge}
                    onChange={(e) => handleInputChange('guardianAge', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${errors.guardianAge ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Age"
                  />
                  {errors.guardianAge && <p className="text-red-500 text-sm mt-1">{errors.guardianAge}</p>}
                  <input
                    type="text"
                    value={formData.guardianOccupation}
                    onChange={(e) => handleInputChange('guardianOccupation', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                    placeholder="Occupation"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Siblings/Age/Class</h3>
                <button
                  onClick={addSibling}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                >
                  + Add Sibling
                </button>
              </div>
              {errors.siblings && <p className="text-red-500 text-sm mb-2">{errors.siblings}</p>}
              <div className="space-y-4">
                {formData.siblings.map((sibling, index) => (
                  <div key={index} className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg items-center">
                    <input
                      type="text"
                      value={sibling.name}
                      onChange={(e) => handleSiblingChange(index, 'name', e.target.value)}
                      className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${!sibling.name.trim() && errors.siblings ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Name"
                    />
                    <input
                      type="number"
                      value={sibling.age}
                      onChange={(e) => handleSiblingChange(index, 'age', e.target.value)}
                      className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${(!sibling.age || Number(sibling.age) <= 0 || Number(sibling.age) > 120) && errors.siblings ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Age"
                    />
                    <input
                      type="text"
                      value={sibling.class}
                      onChange={(e) => handleSiblingChange(index, 'class', e.target.value)}
                      className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${!sibling.class.trim() && errors.siblings ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Class"
                    />
                    <button
                      onClick={() => removeSibling(index)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm flex items-center gap-2"
                      disabled={formData.siblings.length === 1}
                    >
                      <Trash2 size={16} />
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Other Family Members Living Within the Same House</h3>
              <div className="space-y-4">
                {formData.otherFamilyMembers.map((member, index) => (
                  <input
                    key={index}
                    type="text"
                    value={member}
                    onChange={(e) => handleOtherFamilyChange(index, e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder={`Family member ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Information in Case of New Families of the Parents</label>
              <textarea
                value={formData.newFamiliesInfo}
                onChange={(e) => handleInputChange('newFamiliesInfo', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                rows="3"
                placeholder="Enter information about new families"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Economic and Housing Condition</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Source of Income of the Family</label>
                  <textarea
                    value={formData.sourceOfIncome}
                    onChange={(e) => handleInputChange('sourceOfIncome', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${errors.sourceOfIncome ? 'border-red-500' : 'border-gray-300'}`}
                    rows="3"
                    placeholder="Describe the family's source of income"
                  />
                  {errors.sourceOfIncome && <p className="text-red-500 text-sm mt-1">{errors.sourceOfIncome}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">House Condition</label>
                  <textarea
                    value={formData.houseCondition}
                    onChange={(e) => handleInputChange('houseCondition', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${errors.houseCondition ? 'border-red-500' : 'border-gray-300'}`}
                    rows="3"
                    placeholder="Describe the house condition"
                  />
                  {errors.houseCondition && <p className="text-red-500 text-sm mt-1">{errors.houseCondition}</p>}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Main Reasons to Become Sponsored</label>
              <textarea
                value={formData.mainReasons}
                onChange={(e) => handleInputChange('mainReasons', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${errors.mainReasons ? 'border-red-500' : 'border-gray-300'}`}
                rows="5"
                placeholder="Describe the main reasons for sponsorship"
              />
              {errors.mainReasons && <p className="text-red-500 text-sm mt-1">{errors.mainReasons}</p>}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">New Sponsorship Report</h1>
          <p className="text-gray-600">Complete all sections to submit your sponsorship application</p>
        </div>

        {success && <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{success}</div>}
        {errors.form && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">{errors.form}</div>}

        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            {sections.map((section, index) => (
              <div key={index} className="flex items-center flex-1">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${index <= currentSection ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'} font-semibold`}>
                  {index + 1}
                </div>
                {index < sections.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${index < currentSection ? 'bg-blue-600' : 'bg-gray-300'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-gray-600 mt-2">
            {sections.map((section, index) => (
              <div key={index} className="flex-1 text-center px-2">
                {section.title}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">{sections[currentSection].title}</h2>
          {renderSection()}
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className={`px-6 py-3 rounded-lg font-medium transition ${
              currentSection === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            Previous
          </button>

          <div className="flex gap-3">
            <button
              onClick={() => handleSubmit('draft')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center gap-2"
            >
              <Save size={20} />
              Save Draft
            </button>

            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
              >
                Next
                <ChevronRight size={20} />
              </button>
            ) : (
              <button
                onClick={() => handleSubmit('submitted')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
              >
                <Send size={20} />
                Submit Report
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}